import {
  BottomSheetBackdrop,
  BottomSheetFlatList,
  BottomSheetHandle,
  BottomSheetModal,
  BottomSheetTextInput,
  type BottomSheetBackdropProps,
  type BottomSheetFlatListMethods,
  type BottomSheetHandleProps
} from '@gorhom/bottom-sheet'
import { useFocusEffect } from '@react-navigation/native'
import { IconSquare, IconSquareCheck } from '@tabler/icons-react-native'
import { filter, find, includes, isEmpty, join, map, omit, slice, trim, truncate, uniq, without } from 'lodash'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { TextInput, useWindowDimensions, type ListRenderItemInfo } from 'react-native'

import {
  BottomSheetHeader,
  FormInput,
  HStack,
  Icon,
  Input,
  Pressable,
  Text,
  VStack,
  type FormInputProps,
  type InputProps,
  type PressableProps
} from '@/components'
import { useBottomSheetContainerStyle, useFocusableRef } from '@/hooks'
import {
  blackBottomSheetModalBackgroundStyle,
  blackBottomSheetModalHandleIndicatorStyle,
  blackBottomSheetModalHandleStyle
} from '@/lib'

import { type SelectOption } from './types'

const ITEM_HEIGHT = 40 // h-10
const ANY_OPTION_VALUE = 'any'
const ANY_OPTION: SelectOption = { isAny: true, label: 'Any', value: ANY_OPTION_VALUE }

export type MultiSelectInputOthers = Record<string, string>

export interface MultiSelectInputProps extends Omit<FormInputProps, 'children'> {
  inputProps?: Omit<InputProps, 'children' | 'isReadOnly' | 'isDisabled' | 'isFocused'>
  onBlur?: () => void
  onChange: (value: string[]) => void
  onOtherChange?: (value: MultiSelectInputOthers) => void
  options: SelectOption[]
  others?: MultiSelectInputOthers
  otherInputProps?: { testID?: string }
  otherMaxLength?: number
  placeholder?: string
  pressableProps?: Omit<PressableProps, 'children' | 'onPress'>
  value?: string[] | undefined
  withAnyOption?: boolean
}

export type MultiSelectInputRef = {
  focus: () => void
}

export const MultiSelectInput = forwardRef<MultiSelectInputRef, MultiSelectInputProps>(
  (
    {
      accessibilityLabel,
      helper,
      inputProps,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      onOtherChange,
      options,
      others: _others,
      otherInputProps,
      otherMaxLength = 30,
      placeholder = 'Choose Options',
      pressableProps,
      value: _value,
      withAnyOption,
      ...props
    },
    ref
  ) => {
    const dimensions = useWindowDimensions()
    const [buttonRef, focusButton] = useFocusableRef()
    const [headerRef, focusHeader] = useFocusableRef()
    const [otherRef, focusOther] = useFocusableRef<TextInput>()
    const listRef = useRef<BottomSheetFlatListMethods>(null)
    const bottomSheetModalRef = useRef<BottomSheetModal | null>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const onPressOrFocus = useCallback(() => bottomSheetModalRef.current?.present(), [])
    const [others, setOthers] = useState(_others || {})
    const [value, setValue] = useState(_value || [])
    const selected = useMemo(() => filter(options, (option) => includes(value, option.value)), [options, value])
    const listOptions = useMemo(() => (withAnyOption ? [ANY_OPTION, ...options] : options), [options, withAnyOption])
    const [activeOption, setActiveOption] = useState(() => selected[0] || options[0])
    const accessibilityValue = useMemo(() => {
      if (selected.length === 0) {
        return withAnyOption ? 'Any' : 'Nothing selected'
      }

      const head = slice(selected, 0, 5)
      const rest = selected.length - head.length

      return `${selected.length} of ${options.length} selected: ${join(
        map(head, (option) => (option.isOther && others[option.value] ? others[option.value] : option.label)),
        ', '
      )}${rest > 0 ? `, and ${rest} more` : ''}`
    }, [others, options.length, selected, withAnyOption])

    useEffect(() => {
      setOthers(_others || {})
      setValue(_value || [])
    }, [_others, _value])
    useImperativeHandle(ref, () => ({
      ...buttonRef.current,
      focus: onPressOrFocus
    }))

    // @ts-ignore
    return (
      <FormInput
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        <Input
          accessible={false}
          className='h-auto rounded-lg p-2'
          {...inputProps}
        >
          <Pressable
            accessibilityHint={`Tap to open a list of ${label} options`}
            accessibilityLabel={useMemo(
              () =>
                `${accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`}, ${accessibilityValue}`,
              [accessibilityLabel, accessibilityValue, helper, isRequired, label]
            )}
            accessibilityRole='combobox'
            accessibilityState={{
              disabled: isDisabled,
              expanded: isExpanded
            }}
            aria-activedescendent={activeOption?.value}
            aria-haspopup='listbox'
            aria-required={isRequired}
            className='w-full flex-row flex-wrap items-center justify-start gap-2'
            focusable
            onPress={onPressOrFocus}
            ref={buttonRef}
            role='combobox'
            tabIndex={0}
            {...pressableProps}
          >
            {!isEmpty(value) ? (
              map(value, (val) => {
                const option = find(options, (opt) => opt.value === val)

                return option ? (
                  <HStack
                    accessibilityElementsHidden
                    aria-hidden
                    importantForAccessibility='no-hide-descendants'
                    className='items-center justify-center rounded-lg bg-white/15 px-3 py-1'
                    key={option.value}
                  >
                    <Text className=''>
                      {option.isOther && others?.[option.value] ? others[option.value] : option.label}
                    </Text>
                  </HStack>
                ) : null
              })
            ) : (
              <Text
                accessibilityElementsHidden
                aria-hidden
                importantForAccessibility='no-hide-descendants'
                className='px-3 py-1 text-base color-white'
              >
                {placeholder}
              </Text>
            )}
          </Pressable>
        </Input>
        <BottomSheetModal
          accessible={false}
          accessibilityLabel=''
          accessibilityRole='none'
          backdropComponent={useCallback(
            (props: BottomSheetBackdropProps) => (
              <BottomSheetBackdrop
                {...props}
                accessible={false}
                accessibilityLabel=''
                accessibilityRole='none'
                appearsOnIndex={0}
                disappearsOnIndex={-1}
                importantForAccessibility='no-hide-descendants'
              />
            ),
            []
          )}
          backgroundStyle={blackBottomSheetModalBackgroundStyle}
          containerStyle={useBottomSheetContainerStyle()}
          enableDismissOnClose
          enableDynamicSizing
          enablePanDownToClose
          handleComponent={useCallback(
            (props: BottomSheetHandleProps) => (
              <BottomSheetHandle
                {...props}
                accessible={false}
                accessibilityLabel=''
                accessibilityRole='none'
                importantForAccessibility='no-hide-descendants'
              />
            ),
            []
          )}
          handleIndicatorStyle={blackBottomSheetModalHandleIndicatorStyle}
          handleStyle={blackBottomSheetModalHandleStyle}
          importantForAccessibility='no'
          keyboardBehavior='interactive'
          keyboardBlurBehavior='restore'
          maxDynamicContentSize={useMemo(() => dimensions.height * 0.5, [dimensions.height])}
          onChange={useCallback(
            (position: number) => {
              // opened
              if (position === 0) {
                setIsExpanded(true)
                focusHeader()
              }
            },
            [focusHeader]
          )}
          onDismiss={useCallback(() => {
            setIsExpanded(false)
            focusButton()
            onBlur?.()
          }, [focusButton, onBlur])}
          ref={bottomSheetModalRef}
        >
          <BottomSheetFlatList
            accessible={false}
            accessibilityLabel=''
            accessibilityRole='none'
            bounces={false}
            contentContainerClassName='pb-safe-or-4 web:pb-4'
            data={listOptions}
            extraData={{
              others,
              isDisabled,
              isRequired,
              label,
              onChange,
              options,
              value
            }}
            focusHook={useFocusEffect}
            getItemLayout={useCallback(
              (_: any, index: number) => ({
                length: ITEM_HEIGHT,
                offset: ITEM_HEIGHT * index,
                index
              }),
              []
            )}
            keyExtractor={useCallback((item: SelectOption) => item.value, [])}
            onScrollToIndexFailed={useCallback(
              ({
                index,
                averageItemLength
              }: {
                index: number
                highestMeasuredFrameIndex: number
                averageItemLength: number
              }) =>
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: averageItemLength * index
                }),
              []
            )}
            ref={listRef}
            renderItem={useCallback(
              ({ item }: ListRenderItemInfo<SelectOption>) => {
                const isSelected = item.isAny ? isEmpty(value) : includes(value, item.value)
                const otherValue = item.isOther && others?.[item.value] ? others[item.value] : ''
                const otherAccessibilityLabel = item.isOther
                  ? `${item.label} custom text, optional, ${otherMaxLength} characters max`
                  : undefined
                const pressable = (
                  <Pressable
                    // we disable accessibility on the radio option when the user selected "other"
                    // this is so the text input child element becomes accessible
                    accessibilityLabel={item.label}
                    accessibilityRole='checkbox'
                    accessibilityState={{
                      checked: isSelected,
                      disabled: isDisabled
                    }}
                    aria-selected={isSelected}
                    className='flex-1 flex-row items-center justify-between gap-2 px-4 hover:bg-background-50 active:bg-background-100 data-[focus=true]:bg-background-100 web:data-[focus-visible=true]:bg-background-100 web:data-[focus-visible=true]:outline-indicator-primary data-[disabled=true]:opacity-40 data-[disabled=true]:web:pointer-events-auto data-[disabled=true]:web:cursor-not-allowed'
                    onFocus={() => setActiveOption(item)}
                    onPress={() => {
                      if (item.isAny) {
                        // already checked — nothing to clear
                        if (isEmpty(value)) {
                          return
                        }

                        setValue([])
                        onChange([])

                        if (!isEmpty(others)) {
                          setOthers({})
                          onOtherChange?.({})
                        }

                        return
                      }

                      const newValue = isSelected ? without(value, item.value) : uniq([...(value ?? []), item.value])
                      setValue(newValue)
                      onChange(newValue)

                      if (item.isOther) {
                        if (isSelected) {
                          const newOthers = omit(others, item.value)
                          setOthers(newOthers)
                          onOtherChange?.(newOthers)
                        } else if (otherRef.current) {
                          // don't dismiss the bottomsheet when the isOther option is selected, but rather focus the text input
                          focusOther()
                        }
                      }
                    }}
                    style={{
                      height: ITEM_HEIGHT
                    }}
                  >
                    <Text className='color-white'>{item.label}</Text>
                    <Icon
                      as={isSelected ? IconSquareCheck : IconSquare}
                      className='color-white'
                      size='xl'
                    />
                  </Pressable>
                )

                return (
                  <HStack className='items-center justify-center'>
                    {item.isOther && onOtherChange ? (
                      <VStack className='w-full justify-center'>
                        {pressable}
                        <HStack className='items-center gap-3 px-4 pb-4'>
                          <Input className='flex-1'>
                            <BottomSheetTextInput
                              accessibilityLabel={otherAccessibilityLabel}
                              accessibilityState={{
                                disabled: isDisabled
                              }}
                              autoCapitalize='words'
                              autoComplete='off'
                              autoCorrect
                              className='ios:leading-[0px] h-full flex-1 px-3 py-0 font-poppinsRegular text-base text-typography-900 web:cursor-text web:data-[disabled=true]:cursor-not-allowed'
                              editable={!isDisabled}
                              maxLength={otherMaxLength}
                              onChangeText={(val) => {
                                const parsedValue = truncate(val, { length: otherMaxLength, omission: '' })
                                const newOthers = { ...others, [item.value]: parsedValue }

                                setOthers(newOthers)
                                onOtherChange?.(newOthers)

                                if (trim(parsedValue).length > 0) {
                                  // ensure that the other option is selected, if there is other text provided
                                  const newValue = uniq([...value, item.value])
                                  setValue(newValue)
                                  onChange(newValue)
                                }
                              }}
                              onFocus={() => setActiveOption(item)}
                              placeholder='Enter something else here'
                              // @ts-ignore false error
                              ref={otherRef}
                              testID={otherInputProps?.testID}
                              value={otherValue}
                            />
                          </Input>
                          <Text
                            accessible={false}
                            accessibilityElementsHidden
                            aria-hidden
                            importantForAccessibility='no-hide-descendants'
                            className='text-xs color-white'
                          >
                            ({otherValue.length}/{otherMaxLength})
                          </Text>
                        </HStack>
                      </VStack>
                    ) : (
                      pressable
                    )}
                  </HStack>
                )
              },
              [
                value,
                others,
                isDisabled,
                onOtherChange,
                otherRef,
                onChange,
                focusOther,
                otherInputProps?.testID,
                otherMaxLength
              ]
            )}
            ListHeaderComponent={useCallback(
              () => (
                <BottomSheetHeader
                  closeAccessibilityLabel={`Close ${label} options`}
                  onClosePress={() => bottomSheetModalRef.current?.dismiss()}
                  ref={headerRef}
                  title={label ?? 'Options'}
                  titleAccessibilityLabel={`${label} options, ${listOptions.length} items`}
                />
              ),
              [headerRef, label, listOptions.length]
            )}
            stickyHeaderIndices={[0]}
          />
        </BottomSheetModal>
      </FormInput>
    )
  }
)

MultiSelectInput.displayName = 'MultiSelectInput'
